/**
 * ============================================================================
 * 主题风格管理文件 - Theme Configuration
 * ============================================================================
 *
 * 【作用】
 * 统一管理全站样式类名（Tailwind CSS），便于主题切换（亮/暗）和维护。
 * 页面与组件通过引用 theme 或调用本文件导出的函数获取样式，保证风格一致。
 *
 * 【如何修改】
 * - 改颜色：在 colors、gradients、text、textLight 中修改对应字符串（Tailwind 类名）。
 * - 改布局：在 layout、nav、responsive 中修改类名。
 * - 改按钮/卡片：在 buttons、cards 及对应的 Light 版本中修改。
 * - 新增主题项：在 theme 对象下增加键值，再在 getButtonStyle/getCardStyle 等函数中按需使用。
 *
 * 【参数说明】
 * 本文件导出：
 * - theme: 对象，所有样式类名集合。
 * - combineClasses(...classes): 将多个类名字符串合并，自动过滤空值。
 * - getDefaultTheme(): 无参数，根据当前时间返回 'light' 或 'dark'。
 * - getTextColor(themeMode, type): themeMode='light'|'dark', type='primary'|'secondary'|'muted'|'accent'。
 * - getCardStyle(themeMode): 根据主题返回卡片根类名。
 * - getButtonStyle(themeMode, type): themeMode 同上，type='primary'|'secondary'|'small'|'outline'。
 */

export const theme = {
  // -------------------------------------------------------------------------
  // 基础颜色系统
  // 控制全站主色、渐变、文字颜色。修改此处可统一改变站点色调。
  // -------------------------------------------------------------------------
  colors: {
    primary: {
      cyan: "cyan-400",
      purple: "purple-500",
      pink: "pink-400",
      emerald: "emerald-400",
      rose: "rose-400"
    },
    gradients: {
      primary: "from-cyan-400 via-purple-400 to-rose-400",
      cyber: "from-cyan-500 to-purple-600",
      warm: "from-pink-500 to-rose-600",
      cool: "from-cyan-400 to-blue-600",
      deep: "from-purple-500 to-indigo-600",
      fresh: "from-emerald-400 to-teal-600"
    },
    text: {
      primary: "text-white",
      secondary: "text-gray-300",
      muted: "text-gray-400",
      accent: "text-cyan-400"
    },
    textLight: {
      primary: "text-gray-900",
      secondary: "text-gray-700",
      muted: "text-gray-600",
      accent: "text-cyan-600"
    }
  },

  // -------------------------------------------------------------------------
  // 布局组件样式
  // container: 页面主容器最大宽度与内边距；section: 区块上下留白；card: 卡片基底样式。
  // -------------------------------------------------------------------------
  layout: {
    container: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8",
    section: "py-24 px-4",
    card: "bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl",
    cardHover: "hover:border-cyan-500/30 transition-all duration-300"
  },

  // -------------------------------------------------------------------------
  // 导航栏样式（暗色主题）
  // bar: 顶栏固定、背景、边框；container/height: 内层宽度与高度；logo: Logo 容器、光晕、图标圆形容器。
  // -------------------------------------------------------------------------
  nav: {
    bar: "fixed top-0 w-full z-50 border-b border-white/10 bg-black/80 backdrop-blur-xl",
    container: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8",
    height: "h-20",
    logo: {
      container: "flex items-center space-x-3 group cursor-pointer",
      glow: "absolute -inset-1 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full blur opacity-0 group-hover:opacity-75 transition duration-500",
      icon: "relative rounded-full p-0.5 bg-black border border-white/10",
      text: "text-xl font-bold tracking-tighter text-white"
    },
    button: {
      base: "relative px-4 py-2 text-sm font-medium tracking-widest uppercase transition-all duration-300",
      active: "text-white",
      inactive: "text-gray-400 hover:text-cyan-400",
      indicator: "absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-cyan-400 to-purple-500 shadow-[0_0_10px_rgba(6,182,212,0.8)]"
    }
  },

  // -------------------------------------------------------------------------
  // 按钮样式（暗色主题）
  // primary: 主按钮（白底）；secondary: 描边按钮；small/outline: 小号与描边变体。
  // -------------------------------------------------------------------------
  buttons: {
    primary: "px-8 py-4 bg-white text-black font-bold rounded-lg hover:bg-cyan-300 hover:scale-105 transition-all flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(255,255,255,0.3)]",
    secondary: "px-8 py-4 bg-transparent border border-white/20 text-white font-bold rounded-lg hover:bg-white/10 hover:border-white/50 transition-all flex items-center justify-center gap-2 backdrop-blur-sm",
    small: "px-6 py-2 rounded-lg bg-white text-black font-bold text-sm hover:bg-cyan-300 transition-colors",
    outline: "px-6 py-2 rounded-lg border border-white/20 text-white font-bold text-sm hover:bg-white/10 transition-colors",
    tertiary: "px-8 py-4 bg-gray-700 text-white font-bold rounded-lg border border-gray-400 hover:bg-gray-600 hover:text-cyan-300 hover:border-cyan-400 transition-all flex items-center justify-center gap-2 backdrop-blur-sm dark:bg-gray-900 dark:text-white dark:border-gray-500 dark:hover:bg-gray-700 dark:hover:text-cyan-300 dark:hover:border-cyan-400"
  },

  // -------------------------------------------------------------------------
  // 卡片样式（暗色主题）
  // base: 卡片背景、边框、圆角、悬停；glow: 顶部渐变条；content/title/subtitle/tag: 内文与标签样式。
  // -------------------------------------------------------------------------
  cards: {
    base: "group relative bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden hover:border-cyan-500/50 transition-all duration-300",
    glow: "absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 to-purple-500 opacity-50 group-hover:opacity-100 transition-opacity",
    content: "p-8",
    title: "text-2xl font-bold text-white group-hover:text-cyan-300 transition-colors",
    subtitle: "text-gray-400 leading-relaxed",
    tag: "text-xs px-2 py-1 rounded bg-white/5 text-gray-500 border border-white/5"
  },

  animations: {
    fadeIn: "animate-fade-in",
    gradientX: "animate-gradient-x",
    pulse: "animate-pulse",
    bounce: "hover:scale-105 transition-transform",
    glow: "shadow-[0_0_15px_rgba(255,255,255,0.2)]"
  },

  // -------------------------------------------------------------------------
  // 背景效果
  // main/mainLight: 整页字体与选中的基础样式；quantum/quantumLight: 固定背景层；glass/overlay: 玻璃与叠层效果。
  // -------------------------------------------------------------------------
  backgrounds: {
    main: "font-sans text-gray-100 min-h-screen selection:bg-cyan-500 selection:text-black overflow-x-hidden",
    mainLight: "font-sans text-gray-900 min-h-screen selection:bg-cyan-500 selection:text-white overflow-x-hidden",
    quantum: "fixed top-0 left-0 w-full h-full -z-10 bg-[#080808]",
    quantumLight: "fixed top-0 left-0 w-full h-full -z-10 bg-gradient-to-br from-gray-50 to-gray-100",
    glass: "bg-black/60 backdrop-blur-md",
    glassLight: "bg-white/80 backdrop-blur-md",
    overlay: "bg-gradient-to-br from-gray-900 to-black",
    overlayLight: "bg-gradient-to-br from-gray-50 to-white"
  },

  // 导航栏 - 亮色主题（与 nav 结构一致，仅类名改为浅色系）
  navLight: {
    bar: "fixed top-0 w-full z-50 border-b border-gray-200 bg-white/90 backdrop-blur-xl",
    container: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8",
    height: "h-20",
    logo: {
      container: "flex items-center space-x-3 group cursor-pointer",
      glow: "absolute -inset-1 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full blur opacity-0 group-hover:opacity-60 transition duration-500",
      icon: "relative rounded-full p-0.5 bg-white/70",
      text: "text-xl font-bold tracking-tighter text-gray-900"
    },
    button: {
      base: "relative px-4 py-2 text-sm font-medium tracking-widest uppercase transition-all duration-300",
      active: "text-gray-900",
      inactive: "text-gray-600 hover:text-cyan-600",
      indicator: "absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-cyan-500 to-purple-500 shadow-[0_0_10px_rgba(6,182,212,0.8)]"
    }
  },

  // 卡片 - 亮色主题
  cardsLight: {
    base: "group relative bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl overflow-hidden hover:border-cyan-500/50 transition-all duration-300 shadow-sm",
    glow: "absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 to-purple-500 opacity-50 group-hover:opacity-100 transition-opacity",
    content: "p-8",
    title: "text-2xl font-bold text-gray-900 group-hover:text-cyan-600 transition-colors",
    subtitle: "text-gray-600 leading-relaxed",
    tag: "text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 border border-gray-200"
  },

  // 按钮 - 亮色主题
  buttonsLight: {
    primary: "px-8 py-4 bg-gray-900 text-white font-bold rounded-lg hover:bg-gray-800 hover:scale-105 transition-all flex items-center justify-center gap-2 shadow-lg",
    secondary: "px-8 py-4 bg-transparent border border-gray-300 text-gray-900 font-bold rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center gap-2 backdrop-blur-sm",
    small: "px-6 py-2 rounded-lg bg-gray-900 text-white font-bold text-sm hover:bg-gray-800 transition-colors",
    outline: "px-6 py-2 rounded-lg border border-gray-300 text-gray-900 font-bold text-sm hover:bg-gray-50 transition-colors",
    tertiary: "px-8 py-4 bg-gray-700 text-white font-bold rounded-lg border border-gray-400 hover:bg-gray-600 hover:text-cyan-300 hover:border-cyan-400 transition-all flex items-center justify-center gap-2 backdrop-blur-sm dark:bg-gray-900 dark:text-white dark:border-gray-500 dark:hover:bg-gray-700 dark:hover:text-cyan-300 dark:hover:border-cyan-400"
  },

  // 响应式断点：mobile/desktop 控制小屏/大屏显示；grid 为栅格列数（单列到四列）。
  responsive: {
    mobile: "md:hidden",
    desktop: "hidden md:flex",
    grid: {
      single: "grid-cols-1",
      double: "grid-cols-1 md:grid-cols-2",
      triple: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
      auto: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    }
  }
};

/** 根据路径从 theme 中取类名字符串，如 getThemeClass('nav.logo.container') */
export const getThemeClass = (path) => {
  return path.split('.').reduce((obj, key) => obj?.[key], theme) || '';
};

/**
 * 组合多个类名，过滤掉空值后拼接成一行。
 * @param {...string} classes - 任意个类名字符串
 * @returns {string}
 */
export const combineClasses = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

/**
 * 根据当前时间返回默认主题。6:00–18:00 为亮色，其余为暗色。
 * @returns {'light'|'dark'}
 */
export const getDefaultTheme = () => {
  const hour = new Date().getHours();
  return (hour >= 6 && hour < 18) ? 'light' : 'dark';
};

/**
 * 根据主题模式获取文本颜色类名。
 * @param {'light'|'dark'} themeMode
 * @param {'primary'|'secondary'|'muted'|'accent'} type
 * @returns {string} Tailwind 类名
 */
export const getTextColor = (themeMode, type = 'primary') => {
  const isLight = themeMode === 'light';
  if (type === 'primary') {
    return isLight ? theme.colors.textLight.primary : theme.colors.text.primary;
  } else if (type === 'secondary') {
    return isLight ? theme.colors.textLight.secondary : theme.colors.text.secondary;
  } else if (type === 'muted') {
    return isLight ? theme.colors.textLight.muted : theme.colors.text.muted;
  } else if (type === 'accent') {
    return isLight ? theme.colors.textLight.accent : theme.colors.text.accent;
  }
  return isLight ? theme.colors.textLight.primary : theme.colors.text.primary;
};

/**
 * 根据主题模式返回卡片根样式类名（用于页面中的卡片容器）。
 * @param {'light'|'dark'} themeMode
 * @returns {string}
 */
export const getCardStyle = (themeMode) => {
  return themeMode === 'light' ? theme.cardsLight.base : theme.cards.base;
};

/**
 * 根据主题模式与类型返回按钮样式类名。
 * @param {'light'|'dark'} themeMode
 * @param {'primary'|'secondary'|'small'|'outline'} type
 * @returns {string}
 */
export const getButtonStyle = (themeMode, type = 'primary') => {
  const isLight = themeMode === 'light';
  if (type === 'primary') {
    return isLight ? theme.buttonsLight.primary : theme.buttons.primary;
  } else if (type === 'secondary') {
    return isLight ? theme.buttonsLight.secondary : theme.buttons.secondary;
  } else if (type === 'small') {
    return isLight ? theme.buttonsLight.small : theme.buttons.small;
  } else if (type === 'outline') {
    return isLight ? theme.buttonsLight.outline : theme.buttons.outline;
  } else if (type === 'tertiary') {
    return isLight ? theme.buttonsLight.tertiary : theme.buttons.tertiary;
  }
  return isLight ? theme.buttonsLight.primary : theme.buttons.primary;
};
